import { useState } from 'react';
import { validarCPF, formatCpf, formatCardNumber, formatExpiry, detectBrand } from './utils';

const FONT = "'DM Sans', sans-serif";
const FONT_DISPLAY = "'Source Serif 4', 'DM Sans', serif";

type Props = {
  slug: string;
  selectionString?: string;
  plan?: 'musica' | 'video';
  valor: number;
  nomeSugerido?: string;
  cupomCodigo?: string | null;
  onApproved: () => void;
};

type Tipo = 'credit_card' | 'debit_card';

export default function CartaoTab({ slug, selectionString = '', plan, valor, cupomCodigo, onApproved }: Props) {
  const [tipo, setTipo] = useState<Tipo>('credit_card');
  const [parcelas, setParcelas] = useState(1);
  const [numero, setNumero] = useState('');
  const [nome, setNome] = useState('');
  const [validade, setValidade] = useState('');
  const [cvv, setCvv] = useState('');
  const [titularNome, setTitularNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const brand = detectBrand(numero);

  const validar = () => {
    const errs: Record<string, string> = {};
    const num = numero.replace(/\D/g, '');
    if (num.length < 13 || num.length > 19) errs.numero = 'Número do cartão inválido';
    if (!nome.trim() || nome.trim().split(/\s+/).length < 2) errs.nome = 'Nome no cartão como impresso (com sobrenome)';
    if (!/^\d{2}\/\d{2}$/.test(validade)) errs.validade = 'Use MM/AA';
    else {
      const [mm, aa] = validade.split('/').map(Number);
      if (mm < 1 || mm > 12) errs.validade = 'Mês inválido';
      const ano = 2000 + aa;
      const fim = new Date(ano, mm, 0);
      if (fim < new Date()) errs.validade = 'Cartão vencido';
    }
    if (!/^\d{3,4}$/.test(cvv.replace(/\D/g, ''))) errs.cvv = 'CVV inválido';
    if (!titularNome.trim()) errs.titularNome = 'Nome obrigatório';
    if (!validarCPF(cpf)) errs.cpf = 'CPF inválido';
    return errs;
  };

  const errors = validar();
  const formValido = Object.keys(errors).length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValido || submitting) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const r = await fetch('/api/checkout?action=cartao', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug,
          previas_selecionadas: selectionString || undefined,
          plano: plan,
          cupom_codigo: cupomCodigo || '',
          tipo,
          parcelas: tipo === 'credit_card' ? parcelas : 1,
          cartao: { numero, nome, validade, cvv },
          payer: { nome: titularNome, cpf, email },
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (data.ok || ['CONFIRMED', 'RECEIVED', 'APPROVED'].includes(String(data.status || '').toUpperCase())) {
        onApproved();
        return;
      }
      setServerError(humanizeError(data));
    } catch {
      setServerError('Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const valorParcela = (valor / parcelas).toFixed(2).replace('.', ',');
  const valorFmt = valor.toFixed(2).replace('.', ',');

  return (
    <form onSubmit={handleSubmit} style={{ fontFamily: FONT, animation: 'inspiraFadeIn 0.4s' }}>
      {/* Toggle crédito / débito */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 18 }}>
        <ToggleButton ativa={tipo === 'credit_card'} onClick={() => setTipo('credit_card')}>
          Crédito
        </ToggleButton>
        <ToggleButton ativa={tipo === 'debit_card'} onClick={() => setTipo('debit_card')}>
          Débito
        </ToggleButton>
      </div>

      {/* Cartão visual + form integrados */}
      <div
        style={{
          padding: '18px 18px 16px',
          borderRadius: 16,
          background: 'var(--section-alt)',
          border: '1px solid var(--border-soft)',
          marginBottom: 14,
        }}
      >
        <Field label="Número do cartão" error={errors.numero}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="cc-number"
              value={numero}
              onChange={(e) => setNumero(formatCardNumber(e.target.value))}
              placeholder="0000 0000 0000 0000"
              maxLength={23}
              style={inputStyle}
            />
            {brand && (
              <div
                style={{
                  position: 'absolute',
                  right: 14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: 10.5,
                  fontWeight: 800,
                  color: 'var(--text-soft)',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  background: 'var(--cream)',
                  padding: '3px 8px',
                  borderRadius: 6,
                }}
              >
                {brand}
              </div>
            )}
          </div>
        </Field>

        <Field label="Nome no cartão (como impresso)" error={errors.nome}>
          <input
            type="text"
            autoComplete="cc-name"
            value={nome}
            onChange={(e) => setNome(e.target.value.toUpperCase())}
            placeholder="MARIA SILVA"
            style={inputStyle}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Validade" error={errors.validade}>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="cc-exp"
              value={validade}
              onChange={(e) => setValidade(formatExpiry(e.target.value))}
              placeholder="MM/AA"
              maxLength={5}
              style={inputStyle}
            />
          </Field>
          <Field label="CVV" error={errors.cvv}>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="cc-csc"
              value={cvv}
              onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="123"
              maxLength={4}
              style={inputStyle}
            />
          </Field>
        </div>
      </div>

      {/* Dados do titular */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: 10 }}>
        Dados do titular
      </div>

      <Field label="Nome do titular" error={errors.titularNome}>
        <input
          type="text"
          value={titularNome}
          onChange={(e) => setTitularNome(e.target.value)}
          placeholder="Maria Silva"
          style={inputStyle}
        />
      </Field>

      <Field label="CPF do titular" error={errors.cpf}>
        <input
          type="text"
          inputMode="numeric"
          value={cpf}
          onChange={(e) => setCpf(formatCpf(e.target.value))}
          placeholder="000.000.000-00"
          maxLength={14}
          style={inputStyle}
        />
      </Field>

      <Field label="E-mail (pra enviar o comprovante — opcional)">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seu@email.com"
          style={inputStyle}
        />
      </Field>

      {tipo === 'credit_card' && (
        <Field label="Parcelas">
          <select
            value={parcelas}
            onChange={(e) => setParcelas(Number(e.target.value))}
            style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}
          >
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>
                {n}x de R$ {(valor / n).toFixed(2).replace('.', ',')}
                {n === 1 ? ' à vista' : ' sem juros'}
              </option>
            ))}
          </select>
        </Field>
      )}

      {serverError && (
        <div
          style={{
            padding: 12,
            borderRadius: 12,
            background: 'rgba(214,76,43,0.08)',
            color: '#9F2A1F',
            fontSize: 13,
            marginTop: 8,
            lineHeight: 1.5,
          }}
        >
          {serverError}
        </div>
      )}

      <button
        type="submit"
        disabled={!formValido || submitting}
        style={{
          width: '100%',
          marginTop: 18,
          padding: '16px 22px',
          borderRadius: 999,
          background: !formValido || submitting ? 'var(--terra)' : 'var(--gold)',
          color: '#fff',
          fontWeight: 700,
          fontSize: 15.5,
          fontFamily: FONT,
          border: 'none',
          cursor: !formValido || submitting ? 'not-allowed' : 'pointer',
          opacity: !formValido || submitting ? 0.55 : 1,
          boxShadow: '0 8px 28px rgba(255,145,77,0.38)',
          letterSpacing: '-0.005em',
          transition: 'background 0.2s, opacity 0.2s, transform 0.18s',
        }}
        onMouseDown={(e) => formValido && !submitting && (e.currentTarget.style.transform = 'scale(0.98)')}
        onMouseUp={(e) => (e.currentTarget.style.transform = '')}
        onMouseLeave={(e) => (e.currentTarget.style.transform = '')}
      >
        {submitting
          ? 'Processando…'
          : tipo === 'credit_card' && parcelas > 1
            ? `Pagar ${parcelas}x R$ ${valorParcela}`
            : (
              <>
                Pagar <span style={{ fontFamily: FONT_DISPLAY, fontStyle: 'italic', marginLeft: 4 }}>R$ {valorFmt}</span>
              </>
            )}
      </button>

      <p style={{ fontSize: 11, color: 'var(--text-soft)', textAlign: 'center', marginTop: 10, lineHeight: 1.5 }}>
        Pagamento processado pela Asaas. Não armazenamos dados de cartão.
      </p>

      <style>{`@keyframes inspiraFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </form>
  );
}

function humanizeError(data: { error?: string; details?: string }): string {
  if (data?.details) return data.details;
  const map: Record<string, string> = {
    payer_incomplete: 'Preencha nome e CPF do titular para continuar.',
    cartao_incomplete: 'Preencha todos os dados do cartão.',
    cupom_invalido: 'Cupom inválido ou expirado.',
    cartao_recusado: 'Cartão recusado. Confira os dados ou tente outro.',
    selecao_obrigatoria: 'Escolha pelo menos uma versão antes de pagar.',
    pedido_nao_encontrado: 'Pedido não encontrado.',
    pedido_ja_pago: 'Esse pedido já foi pago.',
  };
  return map[data?.error || ''] || 'Não foi possível processar o pagamento.';
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6, letterSpacing: '0.02em' }}>
        {label}
      </label>
      {children}
      {error && <div style={{ color: '#D64C2B', fontSize: 11.5, marginTop: 4, fontWeight: 500 }}>{error}</div>}
    </div>
  );
}

function ToggleButton({ ativa, onClick, children }: { ativa: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '11px 12px',
        borderRadius: 12,
        border: ativa ? '1.5px solid var(--gold)' : '1.5px solid var(--border-soft)',
        background: ativa ? 'var(--gold-pale)' : 'transparent',
        color: ativa ? 'var(--amber-deep)' : 'var(--text-muted)',
        fontWeight: 700,
        fontSize: 13.5,
        fontFamily: FONT,
        cursor: 'pointer',
        transition: 'all 0.18s',
      }}
    >
      {children}
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '14px 14px',
  borderRadius: 12,
  border: '1px solid var(--border-soft)',
  background: 'var(--surface-white)',
  fontSize: 15.5,
  fontFamily: FONT,
  color: 'var(--text-body)',
  boxSizing: 'border-box',
  outline: 'none',
  WebkitAppearance: 'none' as const,
  transition: 'border-color 0.18s, box-shadow 0.18s',
};
